// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  aircraftDisplayLabel,
} from "./aircraft-profile-model.js";
import {
  sampleAircraftTailNumber,
  secondaryLegacySampleAircraftTailNumber,
  secondarySampleAircraftTailNumber,
  tertiaryLegacySampleAircraftTailNumber,
  tertiarySampleAircraftTailNumber,
} from "../core/config.js";

export const supportedFlightParsers = new Set([
  "advanced-flight",
  "insight",
  "insight-g3",
  "insight-g4-twin",
  "ei-mvp",
  "ei-cgr",
  "ei-cgr-combo",
  "ei-cgr-twin",
  "ei-cgr-30c",
  "ei-cgr-30c-legacy",
  "dynon-skyview",
  "garmin-user-log",
  "garmin-g1000",
  "garmin-txi",
  "jpi-csv",
  "jpi-binary",
]);

function replaceSampleAircraftTailText(value) {
  const text = String(value ?? "");
  return text
    .replace(/\bN23508\b/g, sampleAircraftTailNumber)
    .replace(new RegExp(`\\b${secondaryLegacySampleAircraftTailNumber}\\b`, "g"), secondarySampleAircraftTailNumber)
    .replace(new RegExp(`\\b${tertiaryLegacySampleAircraftTailNumber}\\b`, "g"), tertiarySampleAircraftTailNumber);
}

function displayTextWithSampleAliases(value, { preserveLegacySample = false } = {}) {
  if (preserveLegacySample) return String(value ?? "");
  return replaceSampleAircraftTailText(value);
}

export function isSupportedFlight(entry) {
  return supportedFlightParsers.has(entry?.parser);
}

function databaseFlightStatus(entry) {
  return String(
    entry?.supported_status
      ?? entry?.supportedStatus
      ?? entry?.database?.supportedStatus
      ?? entry?.database?.supported_status
      ?? "",
  ).trim().toLowerCase();
}

export function flightReadyToView(entry) {
  if (!entry || entry.source !== "database") return true;
  const status = databaseFlightStatus(entry);
  if (status === "supported") return Boolean(entry.database?.normalizedArtifactId && entry.database?.analysisRunId);
  if (status) return true;
  if (!isSupportedFlight(entry)) return true;
  return Boolean(entry.database?.normalizedArtifactId && entry.database?.analysisRunId);
}

export function flightSourcePaths(entry) {
  const metadata = entry?.segment_metadata && typeof entry.segment_metadata === "object"
    ? entry.segment_metadata
    : {};
  const componentPaths = [
    ...(Array.isArray(entry?.components) ? entry.components : []),
    ...(Array.isArray(metadata.components) ? metadata.components : []),
  ].map((component) => component?.path);
  const paths = [
    entry?.path,
    entry?.auxPath,
    metadata.sourcePath,
    metadata.auxPath,
    ...componentPaths,
  ].filter(Boolean).map(String);
  return [...new Set(paths)];
}

export function flightMatchesSourcePath(entry, sourcePath) {
  const path = String(sourcePath ?? "");
  return Boolean(path) && flightSourcePaths(entry).includes(path);
}

export function flightCodeFromText(value) {
  return String(value ?? "").match(/\b(?:Flt|Log)\d+(?=$|[^\d])/i)?.[0] ?? "";
}

export function compactMonthDayYearIso(monthText, dayText, yearText) {
  const month = Number(monthText);
  const day = Number(dayText);
  if (!Number.isInteger(month) || !Number.isInteger(day) || month < 1 || month > 12 || day < 1 || day > 31) return "";
  const year = Number(yearText);
  const fullYear = year >= 70 ? 1900 + year : 2000 + year;
  return isoDateFromParts(fullYear, month, day);
}

function isoDateFromParts(yearText, monthText, dayText) {
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return "";
  if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return "";
  const candidate = new Date(Date.UTC(year, month - 1, day));
  if (
    candidate.getUTCFullYear() !== year
    || candidate.getUTCMonth() !== month - 1
    || candidate.getUTCDate() !== day
  ) {
    return "";
  }
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function garminLogYearMonthDayIso(value) {
  const match = String(value ?? "").match(/(?:^|[^A-Za-z0-9])log[_ -](\d{2})(\d{2})(\d{2})[_ -]\d{6}/i);
  if (!match) return "";
  const year = Number(match[1]) >= 70 ? 1900 + Number(match[1]) : 2000 + Number(match[1]);
  return isoDateFromParts(year, match[2], match[3]);
}

function textHasValidIsoDate(value) {
  return [...String(value ?? "").matchAll(/\b((?:19|20)\d{2})-(\d{2})-(\d{2})\b/g)]
    .some((match) => Boolean(isoDateFromParts(match[1], match[2], match[3])));
}

function textHasInvalidIsoDate(value) {
  const matches = [...String(value ?? "").matchAll(/\b((?:19|20)\d{2})-(\d{2})-(\d{2})\b/g)];
  return Boolean(matches.length) && !matches.some((match) => Boolean(isoDateFromParts(match[1], match[2], match[3])));
}

const weekdayTokenToIndex = Object.freeze({
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
});

const monthTokenToIndex = Object.freeze({
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
});

function weekdayHintMatch(text) {
  return String(text ?? "").match(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*(\d{1,2})(sun|mon|tue|wed|thu|fri|sat)\b/i);
}

function weekdayHintForMonthDay(text, month, day) {
  if (!Number.isInteger(month) || !Number.isInteger(day) || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const match = weekdayHintMatch(text);
  if (!match) return null;
  const hintedMonth = monthTokenToIndex[String(match[1] ?? "").slice(0, 3).toLowerCase()] ?? null;
  const hintedDay = Number(match[2]);
  const weekdayIndex = weekdayTokenToIndex[String(match[3] ?? "").toLowerCase()] ?? null;
  if (!Number.isInteger(hintedMonth) || !Number.isInteger(hintedDay) || weekdayIndex === null || hintedMonth !== month || hintedDay !== day) return null;
  return weekdayIndex;
}

function dateMatchesHint(year, month, day, weekdayIndex) {
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day) || weekdayIndex === null) return false;
  const candidate = new Date(Date.UTC(year, month - 1, day));
  return candidate.getUTCFullYear() === year
    && candidate.getUTCMonth() === month - 1
    && candidate.getUTCDate() === day
    && candidate.getUTCDay() === weekdayIndex;
}

function hintedYearForMonthDay(text, month, day, { minYear = 2000, maxYear = new Date().getUTCFullYear() + 1 } = {}) {
  const weekdayIndex = weekdayHintForMonthDay(text, month, day);
  if (weekdayIndex === null) return null;
  for (let year = maxYear; year >= minYear; year -= 1) {
    if (dateMatchesHint(year, month, day, weekdayIndex)) return year;
  }
  return null;
}

export function flightDateFromText(value) {
  const text = String(value ?? "");
  const garminLogDate = garminLogYearMonthDayIso(text);
  if (garminLogDate) return garminLogDate;
  const iso = text.match(/(?:^|[^\d])((?:19|20)\d{2})[-_](\d{2})[-_](\d{2})(?:[^\d]|$)/);
  if (iso) {
    const date = isoDateFromParts(iso[1], iso[2], iso[3]);
    if (date) return date;
  }
  const compact = text.match(/(?:^|[^A-Za-z0-9])((?:19|20)\d{2})(\d{2})(\d{2})[A-Za-z]?(?=$|[^A-Za-z0-9])/);
  if (compact) {
    const date = isoDateFromParts(compact[1], compact[2], compact[3]);
    if (date) return date;
  }
  const compactUsWithSequence = text.match(/(?:^|[^A-Za-z0-9])(\d{2})(\d{2})(\d{2})(\d{2})(?=$|[^A-Za-z0-9])/);
  if (compactUsWithSequence) {
    const month = Number(compactUsWithSequence[1]);
    const day = Number(compactUsWithSequence[2]);
    const possibleHour = Number(compactUsWithSequence[3]);
    const possibleMinute = Number(compactUsWithSequence[4]);
    const looksLikeMonthDayTime = possibleHour >= 0 && possibleHour <= 23 && possibleMinute >= 0 && possibleMinute <= 59;
    const candidateYear = Number(compactUsWithSequence[3]) >= 70 ? 1900 + Number(compactUsWithSequence[3]) : 2000 + Number(compactUsWithSequence[3]);
    const weekdayIndex = weekdayHintForMonthDay(text, month, day);
    if (weekdayIndex !== null && !dateMatchesHint(candidateYear, month, day, weekdayIndex)) {
      const hintedYear = hintedYearForMonthDay(text, month, day);
      if (Number.isInteger(hintedYear)) {
        return `${hintedYear}-${compactUsWithSequence[1]}-${compactUsWithSequence[2]}`;
      }
    }
    if (dateMatchesHint(candidateYear, month, day, weekdayIndex)) {
      return `${candidateYear}-${compactUsWithSequence[1]}-${compactUsWithSequence[2]}`;
    }
    if (looksLikeMonthDayTime) return "";
    return compactMonthDayYearIso(compactUsWithSequence[1], compactUsWithSequence[2], compactUsWithSequence[3]);
  }
  const prefixedCompactUs = text.match(/(?:^|[^A-Za-z0-9])[A-Za-z](\d{2})(\d{2})(\d{2})(?=$|[^A-Za-z0-9])/);
  if (prefixedCompactUs) {
    return compactMonthDayYearIso(prefixedCompactUs[2], prefixedCompactUs[3], prefixedCompactUs[1]);
  }
  const us = text.match(/(?:^|[^\d])(\d{1,2})[-_](\d{1,2})[-_](\d{2,4})(?:[^\d]|$)/);
  if (!us) return "";
  const month = Number(us[1]);
  const day = Number(us[2]);
  const candidateYear = us[3].length === 2
    ? (Number(us[3]) >= 70 ? 1900 + Number(us[3]) : 2000 + Number(us[3]))
    : Number(us[3]);
  const weekdayIndex = weekdayHintForMonthDay(text, month, day);
  if (weekdayIndex !== null && !dateMatchesHint(candidateYear, month, day, weekdayIndex)) {
    const hintedYear = hintedYearForMonthDay(text, month, day);
    if (Number.isInteger(hintedYear)) {
      return `${hintedYear}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
    }
  }
  if (dateMatchesHint(candidateYear, month, day, weekdayIndex)) {
    return `${candidateYear}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  }
  if (Number.isInteger(hintedYearForMonthDay(text, month, day)) && !Number.isInteger(candidateYear)) {
    const hintedYear = hintedYearForMonthDay(text, month, day);
    return `${hintedYear}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  }
  const year = us[3].length === 2
    ? String(candidateYear)
    : us[3];
  return `${year}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
}

export function plausibleFlightTimestamp(timestamp, { minYear = 1990, maxFutureYears = 1 } = {}) {
  if (!timestamp) return false;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return false;
  const year = date.getUTCFullYear();
  const currentYear = new Date().getUTCFullYear();
  return year >= minYear && year <= currentYear + maxFutureYears;
}

function isoUtcDateParts(date) {
  return {
    year: date.getUTCFullYear(),
    month: String(date.getUTCMonth() + 1).padStart(2, "0"),
    day: String(date.getUTCDate()).padStart(2, "0"),
    time: `${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")}:${String(date.getUTCSeconds()).padStart(2, "0")}.${String(date.getUTCMilliseconds()).padStart(3, "0")}Z`,
  };
}

export function correctedFlightTimestamp(timestamp, sourceText) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  const sourceDate = flightDateFromText(sourceText);
  const sourceYear = sourceDate ? Number(sourceDate.slice(0, 4)) : null;
  if (!plausibleFlightTimestamp(timestamp)) {
    if (!sourceDate) return null;
    const { time } = isoUtcDateParts(date);
    return `${sourceDate}T${time}`;
  }
  if (Number.isFinite(sourceYear) && Math.abs(date.getUTCFullYear() - sourceYear) >= 5) {
    const { month, day, time } = isoUtcDateParts(date);
    return `${sourceYear}-${month}-${day}T${time}`;
  }
  return date.toISOString();
}

export function stripLikelyOwnerName(value) {
  return String(value ?? "")
    .replace(/((?:Flt|Log)\d+[_ -]+(?:19|20)\d{6}[A-Za-z]?)\s+[A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+)+(?=(?:\.[A-Za-z0-9]+)?(?:$|[ /\u00b7-]))/g, "$1")
    .replace(/((?:19|20)\d{6}[A-Za-z]?)\s+[A-Z][A-Za-z]+(?:\s+[A-Z][A-Za-z]+)+(?=(?:\.[A-Za-z0-9]+)?(?:$|[ /\u00b7-]))/g, "$1")
    .replace(/\s{2,}/g, " ")
    .replace(/\s+\u00b7/g, " \u00b7")
    .trim();
}

export function flightDisplayTitle(entry) {
  const preserveLegacySample = Boolean(entry?.database || entry?.source === "database");
  const title = displayTextWithSampleAliases(String(entry?.title ?? "").trim(), { preserveLegacySample });
  const sanitizedTitle = stripLikelyOwnerName(title);

  let baseLabel;
  if (title && sanitizedTitle === title && !textHasInvalidIsoDate(title)) {
    baseLabel = title;
  } else {
    const aircraft = displayTextWithSampleAliases(aircraftDisplayLabel(entry?.aircraft), { preserveLegacySample });
    const source = `${title} ${displayTextWithSampleAliases(entry?.path ?? "", { preserveLegacySample })} ${displayTextWithSampleAliases(entry?.id ?? "", { preserveLegacySample })}`;
    const label = [flightCodeFromText(source), flightDateFromText(source)].filter(Boolean).join(" ");
    baseLabel = [aircraft, label || sanitizedTitle || "Flight log"].filter(Boolean).join(" · ");
  }

  // Skip if the base label already contains a valid ISO-style date — avoid duplication.
  if (textHasValidIsoDate(baseLabel)) return baseLabel;
  const dateLabel = flightDateTimeLabel(null, entry);
  if (!dateLabel || dateLabel === "Date unavailable") return baseLabel;
  return `${baseLabel} · ${dateLabel}`;
}

export function flightSourcePathDisplay(entry) {
  const path = String(entry?.path ?? "");
  const preserveLegacySample = Boolean(entry?.database || entry?.source === "database");
  const sanitized = stripLikelyOwnerName(displayTextWithSampleAliases(path, { preserveLegacySample }));
  return sanitized || path;
}

function flightDedupDateKey(entry) {
  const source = `${entry?.path ?? ""} ${entry?.title ?? ""} ${entry?.id ?? ""}`;
  const correctedStoredStart = correctedFlightTimestamp(entry?.flight_start_at, source);
  if (correctedStoredStart) return correctedStoredStart.slice(0, 10);
  if (entry?.flight_date) return String(entry.flight_date).slice(0, 10);
  return flightDateFromText(source);
}

function garminDateGroupKey(entry) {
  const aircraft = String(entry?.aircraft ?? "").trim().toUpperCase();
  const date = flightDedupDateKey(entry);
  return aircraft && date ? `${aircraft}|${date}` : "";
}

// Garmin G3X and Dynon SkyView both emit the same "Session Time" CSV that the
// manifest splits into per-run segments, so the whole-file/segment dedup applies
// to either source.
const sessionTimeCsvParsers = new Set(["garmin-user-log", "dynon-skyview"]);

function isSegmentedSessionTimeCsv(entry) {
  return sessionTimeCsvParsers.has(entry?.parser) && Boolean(entry?.segment);
}

function isSupersededSessionTimeWholeFileCandidate(entry) {
  if (!sessionTimeCsvParsers.has(entry?.parser) || entry?.segment || entry?.jpiFlight || entry?.flight_start_at) return false;
  return !/\b\d{1,2}:\d{2}(?::\d{2})?(?:\s*[-–]\s*\d{1,2}:\d{2}(?::\d{2})?)?\b/.test(String(entry?.title ?? ""));
}

export function suppressSupersededGarminWholeFileEntries(flightManifest = []) {
  const segmentedDateKeys = new Set(
    flightManifest
      .filter(isSegmentedSessionTimeCsv)
      .map(garminDateGroupKey)
      .filter(Boolean),
  );
  if (!segmentedDateKeys.size) return flightManifest;
  return flightManifest.filter((entry) => {
    if (!isSupersededSessionTimeWholeFileCandidate(entry)) return true;
    const key = garminDateGroupKey(entry);
    return !key || !segmentedDateKeys.has(key);
  });
}

export function visibleFlightEntries(flightManifest = [], {
  hiddenFlightIds = new Set(),
  limitPerAircraft = Infinity,
} = {}) {
  const visibleEntriesByAircraft = new Map();
  suppressSupersededGarminWholeFileEntries(flightManifest).forEach((entry) => {
    if (hiddenFlightIds.has(entry.id)) return;
    if (!flightReadyToView(entry)) return;
    if (!visibleEntriesByAircraft.has(entry.aircraft)) visibleEntriesByAircraft.set(entry.aircraft, []);
    visibleEntriesByAircraft.get(entry.aircraft).push(entry);
  });

  return [...visibleEntriesByAircraft.values()].flatMap((entries) => entries.slice(-limitPerAircraft));
}

export function visibleFlightEntriesByAircraft(flightManifest = [], {
  hiddenFlightIds = new Set(),
  trendDateValue = () => null,
  limitPerAircraft = Infinity,
} = {}) {
  const entriesByAircraft = new Map();
  visibleFlightEntries(flightManifest, { hiddenFlightIds, limitPerAircraft }).forEach((entry) => {
    if (!entriesByAircraft.has(entry.aircraft)) entriesByAircraft.set(entry.aircraft, []);
    entriesByAircraft.get(entry.aircraft).push(entry);
  });
  entriesByAircraft.forEach((entries) => {
    entries.sort((left, right) => trendDateValue(right, []) - trendDateValue(left, []));
  });
  return entriesByAircraft;
}

export function flightOptionLabel(entry, {
  valveFlag = null,
} = {}) {
  const title = flightDisplayTitle(entry);
  if (!valveFlag) return `${title}${isSupportedFlight(entry) ? "" : " (unsupported)"}`;
  return `${title} - EGT rhythm ${valveFlag.score}: ${valveFlag.note}`;
}

export function dashboardFlightOptionLabel(entry) {
  const title = flightDisplayTitle(entry);
  return `${title}${isSupportedFlight(entry) ? "" : " (unsupported)"}`;
}

export function valveFlagForFlight(entry, valvePatternSummary) {
  return valvePatternSummary?.flaggedFlights?.[entry?.id] ?? null;
}

// Strip the trailing "· Mon DD, YYYY at HH:MM" suffix added by
// flightDisplayTitle so the closed-state dropdown button can stay narrow
// while the open menu still shows the full date.
export function compactFlightOptionLabel(text) {
  if (!text) return "";
  return String(text)
    .replace(/\s+·\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2},\s+\d{4}(?:\s+at\s+\d{2}:\d{2})?/, "")
    .trim();
}

export function dateLabelFromTimestamp(timestamp) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime()) || !plausibleFlightTimestamp(timestamp)) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Phoenix",
  }).format(date);
}

export function dateTimeLabelFromTimestamp(timestamp) {
  const dateLabel = dateLabelFromTimestamp(timestamp);
  if (!dateLabel) return null;
  const timeParts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone: "America/Phoenix",
  }).formatToParts(new Date(timestamp));
  const hour = timeParts.find((part) => part.type === "hour")?.value ?? "00";
  const minute = timeParts.find((part) => part.type === "minute")?.value ?? "00";
  return `${dateLabel} at ${hour}:${minute}`;
}

// JPI binary/CSV exports don't carry a reliable wall-clock takeoff time —
// the parser fills in a synthetic timestamp (~08:03 UTC), and many rows have
// no flight_start_at at all. Either path produces misleading or missing time,
// so date-only is the consistent representation for JPI sources.
function flightHasUnreliableTimeOfDay(entry) {
  const parser = String(entry?.parser ?? entry?.detected_parser ?? "").toLowerCase();
  return parser.startsWith("jpi");
}

function monitorLocalHourMinute(value) {
  const match = String(value ?? "").match(/^(\d{2}):(\d{2})/);
  if (!match || Number(match[1]) > 23 || Number(match[2]) > 59) return null;
  return `${match[1]}:${match[2]}`;
}

export function flightDateTimeLabel(firstPoint, entry) {
  const localClock = monitorLocalHourMinute(entry?.flight_start_local);
  if (localClock) return `${flightDateLabel(firstPoint, entry)} at ${localClock}`;
  if (flightHasUnreliableTimeOfDay(entry)) return flightDateLabel(firstPoint, entry);
  const source = `${entry?.path ?? ""} ${entry?.title ?? ""} ${entry?.id ?? ""}`;
  const correctedPointTimestamp = correctedFlightTimestamp(firstPoint?.timestamp, source);
  const fromPoint = dateTimeLabelFromTimestamp(correctedPointTimestamp);
  if (fromPoint) return fromPoint;
  const correctedStoredStart = correctedFlightTimestamp(entry?.flight_start_at, source);
  const fromStoredStart = dateTimeLabelFromTimestamp(correctedStoredStart);
  if (fromStoredStart) return fromStoredStart;
  // No usable time-of-day — fall back to a date-only label.
  return flightDateLabel(firstPoint, entry);
}

/** Stable Flights-table label using the canonical day and monitor-local clock. */
export function flightDateStartLabel(entry) {
  const date = flightDateLabel(null, entry);
  const localClock = monitorLocalHourMinute(entry?.flight_start_local);
  return localClock ? `${date} · ${localClock}` : date;
}

export function flightDateLabel(firstPoint, entry) {
  const source = `${entry?.path ?? ""} ${entry?.title ?? ""} ${entry?.id ?? ""}`;
  const correctedPointTimestamp = correctedFlightTimestamp(firstPoint?.timestamp, source);
  const fromPoint = dateLabelFromTimestamp(correctedPointTimestamp);
  if (fromPoint) return fromPoint;
  // Prefer the persisted flight_date for the calendar day. flight_start_at is
  // stored UTC by some sources (e.g. Garmin G1000) and local by others, and it
  // arrives over the wire with no timezone designator ("2025-12-23 03:57:45"),
  // so new Date() reads it as browser-local: an evening UTC start then rolls the
  // day forward, and the shown date drifts with the viewer's timezone. flight_date
  // is the unambiguous local day — anchor it at noon UTC so the calendar date is
  // identical in every viewer timezone. (Slice first: the DB serializes flight_date
  // as a full timestamp, e.g. "2025-12-22 07:00:00", not a bare date.)
  const storedDateOnly = String(entry?.flight_date ?? "").slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(storedDateOnly)) {
    const fromStoredDate = dateLabelFromTimestamp(`${storedDateOnly}T12:00:00Z`);
    if (fromStoredDate) return fromStoredDate;
  }
  const correctedStoredStart = correctedFlightTimestamp(entry?.flight_start_at, source);
  const fromStoredStart = dateLabelFromTimestamp(correctedStoredStart);
  if (fromStoredStart) return fromStoredStart;
  const sourceDate = flightDateFromText(source);
  if (sourceDate) return dateLabelFromTimestamp(`${sourceDate}T12:00:00`) ?? "Date unavailable";
  const yymmdd = source.match(/(?:^|[^\d])(\d{2})(\d{2})(\d{2})(?:[^\d]|$)/);
  if (yymmdd) {
    const year = Number(yymmdd[1]) >= 70 ? `19${yymmdd[1]}` : `20${yymmdd[1]}`;
    const date = isoDateFromParts(year, yymmdd[2], yymmdd[3]);
    return date ? dateLabelFromTimestamp(`${date}T12:00:00`) ?? "Date unavailable" : "Date unavailable";
  }
  const isoDate = source.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    const date = isoDateFromParts(isoDate[1], isoDate[2], isoDate[3]);
    if (date) return dateLabelFromTimestamp(`${date}T12:00:00`) ?? "Date unavailable";
  }
  return "Date unavailable";
}

// Human-readable flight route slug for shareable URLs: the flight's local (or
// recorded) start stamp as "2026-05-22-1443", or just the date when no time is
// known. Empty when the entry has no usable date; callers fall back to the id.
// Uniqueness is the caller's concern (append a short id suffix on collision).
export function flightRouteSlug(entry) {
  const stamp = String(entry?.flight_start_local ?? entry?.flight_start_at ?? entry?.flight_date ?? "");
  const match = stamp.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (match) {
    return match[4] ? `${match[1]}-${match[2]}-${match[3]}-${match[4]}${match[5]}` : `${match[1]}-${match[2]}-${match[3]}`;
  }
  // Older records carry no date columns; fall back to the same filename/title
  // date extraction the flight lists use (date-only slug).
  return flightDateFromText(`${entry?.path ?? ""} ${entry?.title ?? ""} ${entry?.id ?? ""}`) ?? "";
}
